import { isPast } from 'date-fns';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Text } from '@fhs-legacy/universal-components';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import { formatLocalizedCurrentWeekDay } from 'utils/dateTime';
import { format } from 'utils/language/format-date';

import {
  ArrowRightIcon,
  CalendarIcon,
  CardWrapper,
  DateLabel,
  DateTimeWrapper,
  DateWrapper,
  DayLabel,
  SectionWrapper,
  TimeLabel,
  TimeWrapper,
} from './styled';
import { IFutureOrderTimeCard } from './types';

export const FutureOrderTimeCard = ({
  date,
  readOnly = false,
  onPress,
  hasError = false,
  hasOfferValidationError = false,
}: IFutureOrderTimeCard) => {
  const { formatMessage, formatTime } = useIntl();
  const { language } = useLocale();
  const { isDelivery } = useServiceModeContext();
  const asapThresholdMinutes = useFlag(LaunchDarklyFlag.ENABLE_ASAP_PICKUP_THRESHOLD, 0);

  const dateConverted = useMemo(() => {
    return date
      ? new Date(date?.year, date?.month, date?.day, date?.hour, date?.minutes)
      : undefined;
  }, [date]);

  const shouldShowAsap = useMemo(() => {
    return (
      asapThresholdMinutes > 0 &&
      dateConverted &&
      dateConverted.getTime() - new Date().getTime() <= asapThresholdMinutes * 60 * 1000
    );
  }, [asapThresholdMinutes, dateConverted]);

  if (hasError || !dateConverted || (!readOnly && isPast(dateConverted))) {
    return (
      <Text>
        {formatMessage({
          id: isDelivery ? 'noDeliveryOptionsAvailable' : 'noPickupOptionsAvailable',
          description: '',
        })}
      </Text>
    );
  }

  const dateFormatted = format(dateConverted, 'PP', language);
  const dateTimeFormatted = formatTime(dateConverted);
  const dayFormatted = formatLocalizedCurrentWeekDay({
    dateTime: dateConverted,
    language,
    formatMessage,
  });

  const handleCardPress = () => {
    if (readOnly) {
      return;
    }
    onPress && onPress();
  };

  return (
    <SectionWrapper onPress={handleCardPress} $readOnly={readOnly} testID="future-order-time-card">
      <CardWrapper hasError={hasOfferValidationError}>
        <CalendarIcon hasError={hasOfferValidationError} />
        <DateTimeWrapper $readOnly={readOnly}>
          <DateWrapper>
            <DayLabel hasError={hasOfferValidationError}>{dayFormatted}</DayLabel>
            <DateLabel hasError={hasOfferValidationError}>{dateFormatted}</DateLabel>
          </DateWrapper>
          <TimeWrapper>
            <TimeLabel hasError={hasOfferValidationError}>
              {shouldShowAsap ? 'ASAP' : dateTimeFormatted}
            </TimeLabel>
          </TimeWrapper>
        </DateTimeWrapper>
        {!readOnly && <ArrowRightIcon hasError={hasOfferValidationError} />}
      </CardWrapper>
    </SectionWrapper>
  );
};
